import { Box, Flex, ModalCloseButton, ModalHeader, Text } from "@chakra-ui/react";
import type { ReactNode } from "react";

interface Props {
  title: string;
  /**
   * Explanatory prose under the title — what to do here ("Fyll ut type, navn og beskrivelse …") or
   * a rule that governs the form ("Underkriterievektene fordeles innenfor kriteriet"). Which
   * criterion a dialog belongs to is part of its title, not this.
   */
  subtitle?: ReactNode;
  /** Rendered under the subtitle, still above the divider — the library panel's tab row. */
  children?: ReactNode;
}

/**
 * The heading every dialog that writes a requirement shares: title, optional muted subtitle, a close
 * button on the title's line, and one rule under the whole block.
 *
 * Chakra builds Drawer on Modal, so `ModalHeader`/`ModalCloseButton` are what a `Drawer` renders
 * too — one component covers the side panels and the library's centred modals alike.
 */
export const RequirementDialogHeader = ({ title, subtitle, children }: Props) => (
  // The rule lives on the wrapper, not on ModalHeader, so it falls below the subtitle instead of
  // between it and the title.
  <Box borderBottomWidth="1px" borderColor="smBorder">
    <ModalHeader pb={subtitle || children ? "1" : undefined}>
      <Flex align="center" justify="space-between" gap="3">
        <Text fontSize="md" fontWeight="bold">
          {title}
        </Text>
        {/* Chakra's own close button is absolutely positioned, which pins it to the dialog's top
            corner and leaves it floating beside a two-line heading. In flow it sits on the title.

            Never disabled, on purpose — and no prop to make it so. None of these dialogs sets
            closeOnEsc, so Esc always dismissed them anyway; a greyed-out X only made the obvious way
            out harder than the hidden one. */}
        <ModalCloseButton position="static" flexShrink={0} />
      </Flex>
    </ModalHeader>
    {/* Outside ModalHeader on purpose: Chakra wires the dialog's aria-labelledby to that element, so
        keeping the subtitle out leaves the accessible name as the title rather than the title plus a
        whole instruction. */}
    {subtitle && (
      <Text px="6" pb={children ? "2" : "4"} fontSize="sm" color="smMuted">
        {subtitle}
      </Text>
    )}
    {children && (
      <Box px="6" pb="0">
        {children}
      </Box>
    )}
  </Box>
);
